<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else-if="canShow"
      :isSearchable="false"
      :headers="tableHeaders"
      :items="displayedReports"
      :pagination="pagination"
      :loading="loading"
      :title="$t('labReports.labels.reports')"
      @iconAction="downloadReport"
      @sendQueryParams="fetchQuery"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :searchPlaceholder="$t('laboratory.placeholder.labWoSearchBy')"
          :filters="filters"
          :filterName="filterName"
          :saveFilter="true"
          @onApplyFilters="handleApplyFilters"
          @onSearch="handleSearch"
        />
      </template>
      <template #item.action="{item}">
        <v-menu offsetY>
          <template v-slot:activator="{ on, attrs }">
            <div class="action-buttons">
              <v-btn
                color="primary"
                fab
                xSmall
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>
                  mdi-dots-vertical
                </v-icon>
              </v-btn>
            </div>
          </template>

          <v-list>
            <ListItem
              class="report"
              icon="mdi-file-pdf-box"
              title="PDF"
              @click="getCurrentPDF(item)"
            />
            <ListItemMenu
              class="history"
              icon="mdi-history"
              :title="$t('sites.versionsHistory')"
              :items="reportsHistory"
              @click="getReportsHistory(item)"
              @selected-option="downloadReport"
            />
            <div
              class="v-list-item v-list-item--link theme--light"
              :class="{ 'disabled-history': !hasHistory(item.index) }"
              @click="hasHistory(item.index) ? showHistoryData(item.index) : null"
            >
              <v-icon>mdi-archive</v-icon>
              <span class="text-adjust">{{ $t('labReports.labels.history') }}</span>
            </div>
          </v-list>
        </v-menu>
      </template>
      <template #item.woNumber="{item}">
        <v-btn
          text
          small
          class="pl-0"
          @click.stop="onClickRedirectWO(item)"
        >
          <span
            class="mr-1"
            style="text-decoration: underline;"
          >{{ item.woNumber }}</span>
        </v-btn>
      </template>
    </TableRemote>
    <NoPermissionAlert
      v-else
      :message="$t('permissions.error')"
    />
    <LoadingWidget
      :show="showLoadingWidget"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ListItem from '@/components/shared/widgets/ListItem';
import ListItemMenu from '@/components/shared/widgets/ListItemMenu';
import LoadingWidget from '@/components/shared/widgets/LoadingWidget';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import roles from '@/utilities/enums/rolesTypes';
import moment from 'moment';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import filterMixin from '@/mixins/filterMixin';
import HistoryModal from '@/components/shared/HistoryModal.vue';


export default {
    components: {
        LoadingWidget,
        TableRemote,
        NoPermissionAlert,
        ListItem,
        ListItemMenu,
        SearchWithFilters,
    },
    mixins: [
        filterMixin,
    ],
    data() {
        return {
            loading: false,
            isInitializing: true,
            showLoadingWidget: false,
            reports: [],
            reportsHistory: [],
            pagination: {},
            filterName: 'labReports',
            queryParams: {
                perpage: 25,
                startDate: moment().subtract(30, 'days').toISOString(),
                endDate: moment().endOf('day').toISOString(),
            },
            filters: [
                {
                    key: 'date',
                    label: 'laboratory.labels.date',
                    type: 'date',
                    value: [moment().subtract(30, 'days').toISOString(), moment().toISOString()],
                    dateKeys: ['startDate', 'endDate'],
                    isRange: true,
                    datacy: 'LabReportDate',
                },
            ],
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'hasRole',
            'hasPermission',
        ]),
        tableHeaders() {
            const headers = [
                'site',
                'name',
                'woNumber',
                'type',
                'author',
                'createdAt',
            ].map(value => ({
                sortable: false,
                text: this.$t(`labReports.labels.${value}`),
                value,
            }));
            headers.push({
                text: this.$t('controls.actions'),
                value: 'action',
                sortable: false,
            });
            return headers;
        },
        displayedReports() {
            return this.reports.map((report, index) => ({
                ...report,
                index,
                name: report.labWorkOrder.reportName,
                author: `${report.createdBy.firstName} ${report.createdBy.lastName}`,
                type: report.type ? report.type.label : '',
                createdAt: this.$options.filters.formatDate(report.createdAt, 'L', report.site.tz),
                woNumber: report.labWorkOrder.woNumber,
                site: `${report.site.name} [${report.site.deliveryAccountNumber}]`,
            }));
        },

        canShow() {
            return (this.hasPermission('labReports.view') && this.hasPermission('reports.view')) || this.hasPermission('clients.labReports.view');
        },
    },

    watch: {
        async lang() {
            await this.getLabReports();
        },
    },

    async created() {
        this.getFilterValues();
        await this.getLabReports();
        this.isInitializing = false;
    },

    methods: {
        hasHistory(index) {
            const report = this.reports[index];
            return report && report.labWorkOrder && report.labWorkOrder.history && report.labWorkOrder.history.length > 0;
        },
        showHistoryData(index) {
            const report = this.reports[index];

            const historyItems = report.labWorkOrder.history.map(historyEntry => ({
                date: this.$options.filters.formatDate(historyEntry.createdAt, 'L LT'),
                details: historyEntry.manipulatedDetails,
                action: historyEntry.action,
            }));

            this.$root.$emit('showModal', {
                title: this.$t('labReports.labels.history'),
                component: HistoryModal,
                payload: { itemsForHistory: historyItems },
                showConfirmButton: false,

            });
        },
        async getLabReports() {
            this.loading = true;
            const { data } = this.hasRole(roles.SALESREP) || this.hasRole(roles.SALESMANAGER) || this.hasRole(roles.SERVICETECH)
                ? await this.$rq.getClientsLabReports(this.queryParams)
                : await this.$rq.getLabReports(this.queryParams);
            this.reports = data.reports.filter(report => report.labWorkOrder);
            this.pagination = data.pagination;

            this.loading = false;
        },

        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams };
            await this.getLabReports();
        },

        async downloadReport({ file }) {
            let { id, fileName } = file;
            // need this check since we have two different types of object passed in this functiom
            // eslint-disable-next-line no-underscore-dangle
            if (file._id) {
                // eslint-disable-next-line no-underscore-dangle
                id = file._id;
                fileName = file.fileName;
            }
            this.showLoadingWidget = true;
            const { data } = await this.$rq.downloadFile(id);
            this.$utils.saveFileOnDisk(data, fileName);
            this.showLoadingWidget = false;
        },

        async getReportsHistory(report) {
            const { data } = await this.$rq.getLabWorkOrder(report.labWorkOrder.id);
            this.reportsHistory = data.reports.map(entry => ({
                ...entry, name: entry.file.fileName,
            }));
        },
        async getCurrentPDF(report) {
            this.showLoadingWidget = true;
            const { data } = await this.$rq.getLabWorkOrder(report.labWorkOrder.id);
            await this.downloadReport(data.lastReport);
            this.showLoadingWidget = false;
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.getLabReports();
        },
        handleApplyFilters(filterParams) {
            this.queryParams = {
                ...this.queryParams,
                ...filterParams,
            };

            // reset filter if needed
            if (filterParams?.reset) {
                this.getFilterValues(true);
            }
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.getLabReports();
        },
        async onClickRedirectWO(item) {
            this.$router.push({
                name: 'labWorkOrderView',
                params: {
                    labWorkOrderId: item.labWorkOrder.id,
                    clientId: item.site.clientId,
                    siteId: item.site.clientId,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>

.text-adjust {
  flex-grow: 1;
  font-size: 16px;
}
.disabled-history {
  pointer-events: none;
  color: grey;
  opacity: 0.5;}
</style>;
